import React from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  CUSTOM_MARKER_OWN_SECTION_LABEL,
  CustomMarkerSection,
  practitionerSectionFromOptionValue,
  practitionerSectionKey,
  practitionerSectionProblem,
} from '../../models';
import '../bloodworkz/custom-markers-table.css';

/**
 * Validation for the section list, keyed by section id so each message renders
 * against the row it is about. Save holds a section that does not validate back
 * and commits the rest of the page around it, so the message has to be able to
 * find its row. The messages are self-contained (they name the section)
 * because nothing resolves a label for these keys.
 */
export function sectionProblemsById(
  sections: CustomMarkerSection[],
): { [sectionId: string]: string } {
  const byId: { [sectionId: string]: string } = {};
  const list = sections || [];
  list.forEach((section, index) => {
    if (!(section.name || '').trim()) {
      return;
    }
    // Only the sections ABOVE this one, so a repeated name is reported against
    // the row that repeated it rather than against both. Checked against the
    // whole list, typing a name a section already has put an identical error on
    // the section that was there first and correct, and held it back from
    // saving along with the duplicate. This is also how the marker list reads a
    // name already taken (see duplicateNameProblems).
    const problem = practitionerSectionProblem(
      section.name,
      list.slice(0, index),
      section.id,
    );
    if (problem) {
      byId[section.id] = problem;
    }
  });
  return byId;
}

/**
 * Correct a section's name in the list, matched on its current name. Adds it
 * when it is not there — a name typed on a test but never saved to the list is
 * still a section the practitioner is using.
 */
export function renameInSections(
  sections: CustomMarkerSection[],
  from: string,
  to: string,
  newId: () => string,
): CustomMarkerSection[] {
  const trimmed = (to || '').trim();
  if (!trimmed) {
    return sections;
  }
  const fromKey = practitionerSectionKey(from);
  const existing = sections || [];
  const match = existing.find(
    section => practitionerSectionKey(section.name) === fromKey,
  );
  if (!match) {
    return [...existing, new CustomMarkerSection({ id: newId(), name: trimmed })];
  }
  return existing.map(section =>
    section === match
      ? new CustomMarkerSection({ id: section.id, name: trimmed })
      : section,
  );
}

/** Blank rows are dropped rather than saved as nameless sections. */
export function sectionsFromRows(
  sections: CustomMarkerSection[],
): CustomMarkerSection[] {
  return (sections || [])
    .filter(section => (section.name || '').trim())
    .map(
      section =>
        new CustomMarkerSection({
          id: section.id,
          name: section.name.trim(),
        }),
    );
}

/**
 * How many library markers are filed under each section, keyed by section id.
 *
 * Supplied to this component rather than worked out inside it: it holds the
 * section list, and the markers filed under a section live a card away on the
 * page that owns both. Matched the way every other section comparison is, on
 * the normalized name, because a marker stores the NAME it was filed under and
 * never a reference to the section record.
 *
 * A section still being named counts nothing. Its key is blank, and a blank key
 * matches every marker left in Other Lab Results, which would put a
 * "12 markers" warning on an empty row nobody has finished typing.
 */
export function markerCountsBySection(
  sections: CustomMarkerSection[],
  rows: { section: string }[],
): { [sectionId: string]: number } {
  const counts: { [sectionId: string]: number } = {};
  // A name typed twice is still ONE section to every marker filed under it, so
  // the markers are counted against the first entry and the duplicate reads
  // zero. Counting them twice put the same "12 markers" warning on both rows
  // and made the copy look like a panel of its own worth keeping.
  const claimed = new Set<string>();
  (sections || []).forEach(section => {
    const key = practitionerSectionKey(section.name);
    counts[section.id] =
      key && !claimed.has(key)
        ? (rows || []).filter(
            row =>
              practitionerSectionKey(
                practitionerSectionFromOptionValue(row.section),
              ) === key,
          ).length
        : 0;
    if (key) {
      claimed.add(key);
    }
  });
  return counts;
}

type Props = {
  sections: CustomMarkerSection[];
  onChange: (sections: CustomMarkerSection[]) => void;
  /** Supplied by the caller so this component stays free of id generation. */
  newSectionId: () => string;
  /** What Save found wrong, keyed by section id, rendered under its row. */
  errors?: { [sectionId: string]: string };
  /** Markers filed under each section, keyed by section id. */
  markerCounts?: { [sectionId: string]: number };
  /**
   * Remove a section and say what becomes of the markers filed under it. The
   * disposition means nothing for a section nobody has filed anything under,
   * which removes on the click and passes 'move'.
   */
  onRemove: (section: CustomMarkerSection, markers: 'move' | 'delete') => void;
};

/**
 * "My Sections" — report sections the practitioner named themselves, for panels
 * LabSmarts has no home for.
 *
 * Sits BELOW the marker list. It led the page while a section had to exist
 * before a marker could be filed into it, but the Section dropdown creates them
 * inline now, on this page and while entering a test — so nobody comes here to
 * set one up, only to rename or remove one. Setup work goes where it is needed;
 * what is left is maintenance, and maintenance does not get the top of a page
 * named for something else.
 */
export const CustomMarkerSections: React.FC<Props> = ({
  sections,
  onChange,
  newSectionId,
  errors,
  markerCounts,
  onRemove,
}) => {
  const rows = sections || [];
  const counts = markerCounts || {};
  /*
   * What to say under each row: the rules checked as the name is TYPED, plus
   * anything the last Save added on top.
   *
   * A name a section already has was only ever reported by Save, which is a
   * long way from where it is made — the list showed two identical rows and
   * said nothing until the button was pressed, and by then the practitioner had
   * moved on to the markers. Every rule here is about the one field this row
   * has, and a field with something in it can be judged, so there is nothing to
   * hold until Save the way a half-filled range on a marker has to be.
   *
   * Save's messages last, so a row it refused keeps that message until it is
   * edited (handleSectionsChange clears it), and the two never stack up: they
   * are the same rules in the same words.
   */
  const problems = { ...sectionProblemsById(rows), ...(errors || {}) };
  // The section the trash was pressed on while markers were still filed under
  // it. Removing it silently is what made the trash look broken: the heading is
  // built from the markers, so it stayed on the page with its markers under it
  // and only the entry in this list disappeared.
  const [pending, setPending] = React.useState<CustomMarkerSection | undefined>(
    undefined,
  );
  const pendingCount = pending ? counts[pending.id] || 0 : 0;
  const pendingName = (pending?.name || '').trim();

  const update = (index: number, name: string) => {
    onChange(
      rows.map((section, i) =>
        i === index ? new CustomMarkerSection({ ...section, name }) : section,
      ),
    );
  };

  return (
    <div>
      {/* Heading, description, rule — the shape Report Settings gives each of
          its blocks, so the two things this page holds read as two things. */}
      {/* Named by the card around it. */}
      <p style={{ fontSize: '13px', color: '#212529', marginBottom: '10px' }}>
        Sections of your own, for panels LabSmarts does not cover yet (an IgG
        allergen panel, say). They appear in the Section list wherever you add a
        marker, and print after the LabSmarts sections under the name you give
        them. Renaming or removing one here leaves tests you have already saved
        exactly as they were.
      </p>
      {rows.length ? (
        <div>
          {rows.map((section, index) => {
            const problem = problems[section.id];
            return (
              <div
                key={section.id}
                id={`my-section-row-${section.id}`}
                style={{ marginBottom: '6px' }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                  }}
                >
                  <Form.Control
                    type="text"
                    className="custom-marker-input"
                    style={{
                      fontSize: '13px',
                      height: '31px',
                      maxWidth: '320px',
                    }}
                    placeholder="e.g. IgG Allergens"
                    value={section.name}
                    onChange={(e: any) => update(index, e.target.value)}
                  />
                  <Button
                    variant="link"
                    size="sm"
                    title="Remove this section"
                    style={{ color: '#766d66', padding: '0 4px' }}
                    onClick={() => {
                      // Nothing filed under it: nothing to ask about.
                      if ((counts[section.id] || 0) < 1) {
                        onRemove(section, 'move');
                        return;
                      }
                      setPending(section);
                    }}
                  >
                    <FontAwesomeIcon icon="trash-alt" />
                  </Button>
                  {/* Said before the trash is pressed, not only after: a
                      section with a panel of markers under it is not the same
                      click as an empty one. */}
                  {counts[section.id] ? (
                    <span style={{ fontSize: '12px', color: '#6c757d' }}>
                      {counts[section.id]}{' '}
                      {counts[section.id] === 1 ? 'marker' : 'markers'}
                    </span>
                  ) : null}
                </div>
                {problem ? (
                  <div style={{ fontSize: '12px', color: '#c0392b' }}>
                    {problem}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      ) : (
        <p style={{ fontSize: '13px', color: '#6c757d', marginBottom: '10px' }}>
          You have no sections of your own yet.
        </p>
      )}

      {/* Add only. Saving is the page's one Save button: a second one here said
          "Sections", so a practitioner who had just edited a marker and reached
          for the nearest button was told "Sections saved." while their marker
          edit sat uncommitted. */}
      <Button
        variant="outline-primary"
        size="sm"
        style={{ whiteSpace: 'nowrap' }}
        onClick={() =>
          onChange([
            ...rows,
            new CustomMarkerSection({ id: newSectionId(), name: '' }),
          ])
        }
      >
        + Add a section
      </Button>

      {/* Asked rather than assumed. Deleting the section is one answer and
          keeping the definitions is the other, and which one is meant depends
          on why the section is going: a panel dropped from practice takes its
          markers with it, a section named twice by mistake must not. */}
      {pending ? (
        <Modal show onHide={() => setPending(undefined)}>
          <Modal.Header
            closeButton
            style={{ backgroundColor: '#337ab7', color: '#fff' }}
          >
            <Modal.Title style={{ fontSize: '18px' }}>
              Remove &ldquo;{pendingName}&rdquo;?
            </Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ fontSize: '14px' }}>
            <p>
              {pendingCount} {pendingCount === 1 ? 'marker' : 'markers'} in your
              library {pendingCount === 1 ? 'is' : 'are'} filed under{' '}
              <strong>{pendingName}</strong>. What should happen to{' '}
              {pendingCount === 1 ? 'it' : 'them'}?
            </p>
            <ul style={{ paddingLeft: '18px', marginBottom: '12px' }}>
              <li style={{ marginBottom: '4px' }}>
                <strong>Move</strong> keeps{' '}
                {pendingCount === 1 ? 'the marker' : 'the markers'} and files{' '}
                {pendingCount === 1 ? 'it' : 'them'} under{' '}
                {CUSTOM_MARKER_OWN_SECTION_LABEL}, where you can place{' '}
                {pendingCount === 1 ? 'it' : 'them'} again later.
              </li>
              <li>
                <strong>Delete</strong> removes the section and{' '}
                {pendingCount === 1 ? 'that marker' : 'those markers'} from your
                library.
              </li>
            </ul>
            <p style={{ fontSize: '13px', color: '#6c757d', marginBottom: 0 }}>
              Either way, tests you have already saved are left exactly as they
              are, and nothing is written until you save this page.
            </p>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="link"
              style={{ color: '#6c757d' }}
              onClick={() => setPending(undefined)}
            >
              Cancel
            </Button>
            <Button
              variant="outline-danger"
              onClick={() => {
                onRemove(pending, 'delete');
                setPending(undefined);
              }}
            >
              Delete {pendingCount === 1 ? 'the marker' : 'the markers'} too
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                onRemove(pending, 'move');
                setPending(undefined);
              }}
            >
              Move to {CUSTOM_MARKER_OWN_SECTION_LABEL}
            </Button>
          </Modal.Footer>
        </Modal>
      ) : null}
    </div>
  );
};
